// Connected container — fetches relationship detail on demand
// (`useCaseGraphRelationshipDetail`). Not storyable (needs QueryClient).

import { ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useCaseGraphRelationshipDetail } from '@/hooks/use-case-queries'
import type { DocumentRelationship } from '@/services/api/cases'
import { InlineError } from './InlineError'
import { InspectorSkeleton } from './InspectorSkeleton'
import { RelationshipPropertiesList } from './RelationshipPropertiesList'

// Phone: a long unbreakable endpoint name wraps instead of pushing the other
// endpoint out of the row.
const ENDPOINT_NAME_CLASS = 'max-md:min-w-0 max-md:wrap-anywhere'

export function RelationshipRow({ caseId, rel }: { caseId: string; rel: DocumentRelationship }) {
  const [expanded, setExpanded] = useState(false)
  const detailQuery = useCaseGraphRelationshipDetail(caseId, rel.id, expanded)
  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded(p => !p)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded(p => !p)
          }
        }}
        style={{
          padding: '11px 14px',
          cursor: 'pointer',
          background: expanded ? 'var(--muted)' : 'transparent',
          fontFamily: 'var(--font-mono)',
          fontSize: 12.5,
          color: 'var(--foreground)',
          letterSpacing: 0.2,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          className={ENDPOINT_NAME_CLASS}
          style={{ color: 'var(--foreground)', fontWeight: 600 }}
        >
          {rel.source_name ?? '—'}
        </span>
        <ChevronRight size={12} style={{ color: 'var(--primary)', flexShrink: 0 }} />
        <span
          className={ENDPOINT_NAME_CLASS}
          style={{ color: 'var(--foreground)', fontWeight: 600 }}
        >
          {rel.target_name ?? '—'}
        </span>
      </div>
      {expanded && (
        <div
          style={{
            padding: '12px 14px 16px 14px',
            background: 'var(--muted)',
            borderTop: '1px solid var(--border)',
          }}
        >
          {detailQuery.isLoading ? (
            <InspectorSkeleton />
          ) : detailQuery.isError ? (
            <InlineError onRetry={() => detailQuery.refetch()} />
          ) : detailQuery.data ? (
            <RelationshipPropertiesList rel={detailQuery.data} />
          ) : null}
        </div>
      )}
    </div>
  )
}
